import { useSuspenseQuery, type MutationFunction } from "@tanstack/react-query";
import { Flex, OnboardingLayout } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";

import { getCustomerBusinessModelQueryOptions } from "../../../queries/customer-data/queries";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { getAccountingThreshold } from "../../../utils/funding-journey";
import { useDocumentNavigationActions, MESSAGES } from "../components";
import { BalanceSheetDocumentUpload } from "../components/balance-sheet-document-upload";

export const BalanceSheetDocumentUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationData } = useFundingApplication(customerDomain);
  const { data: businessModel } = useSuspenseQuery(getCustomerBusinessModelQueryOptions(customerDomain));
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: uploadedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplicationData.id);
  const { mutate: triggerValidation } = useTriggerValidation(
    customerDomain,
    fundingApplicationData.id,
    UploadDocumentType.BALANCE_SHEET
  );

  const filteredDocumentTypes = uploadedDocuments?.filter(
    (doc) => doc.documentType === UploadDocumentType.BALANCE_SHEET
  );
  const filteredFiles = filteredDocumentTypes?.flatMap(({ data }) => data);
  const hasUploadedDocuments = Boolean(filteredFiles?.length);

  const { primaryAction, secondaryAction, applicationSubmit, documentError } = useDocumentNavigationActions({
    forType: UploadDocumentType.BALANCE_SHEET,
    onPrimaryAction: () => filteredFiles?.length && triggerValidation(filteredFiles.map(({ id }) => id)),
  });

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.balanceSheetTitle)}
        actions={{ primary: primaryAction, secondary: secondaryAction }}
      >
        <Flex maxWidth="550px">
          <BalanceSheetDocumentUpload
            fundingApplication={fundingApplicationData}
            mutate={fundingApplicationMutation.mutate as MutationFunction<FormattedFundingApplicationScreenUpdate>}
            accountingThreshold={getAccountingThreshold(businessModel)}
            hasUploadedDocuments={hasUploadedDocuments}
            error={documentError}
          />
        </Flex>
      </OnboardingLayout>
    </>
  );
};
